import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import { Box, Button, Divider, Typography } from '@mui/material';
import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid';
import { DataGrid, DataGridProps } from '@mui/x-data-grid';
import { ReactNode, useCallback, useMemo } from 'react';

// Remove the properties that we end up manually setting as part of setting up the data control
export type ModifiedDataGridProps = Omit<
  DataGridProps,
  'columns' | 'rows' | 'hideFooterSelectedRowCount' | 'autoPageSize'
>;

/**
 * A properties for a data control component
 */
interface DataControlProps {
  /**
   * The default page title for the grid view.
   */
  pageTitle: string;

  /**
   * The column definition for the data grid
   */
  gridColumns: GridColDef<GridValidRowModel>[];

  /**
   * The row data
   */
  rowData: GridValidRowModel[];

  /**
   * (Optional) Additional props to pass to the DataGrid.
   */
  dataGridProps?: ModifiedDataGridProps;

  // -------------- General Add/Edit Form Support --------------
  /**
   * (Optional) Callback to reset the add/edit form.
   */
  resetForm?: () => void;

  // -------------- Add Form Support --------------
  /**
   * The title to use for the add form (if applicable).
   */
  addTitle?: string;

  /**
   * The form for adding new data. If omitted, the add functionality is disabled.
   */
  addForm?: ReactNode;

  /**
   * True, if the view should show. False, otherwise. Should be controlled
   *  by the calling code.
   */
  addShowing?: boolean;

  /** A function to show the add form */
  showAddForm?: () => void;

  /**
   * Hide the current add. Called when the back button is clicked, if the add
   *  was showing.
   */
  hideAdd?: () => void;

  // -------------- View Form Support --------------
  /**
   * The title to use for the view (if applicable).
   */
  viewTitle?: string;

  /**
   * The content to show. If omitted, the add functionality is disabled.
   */
  viewContent?: ReactNode;

  /**
   * True, if the view should show. False, otherwise. Should be controlled
   *  by the calling code.
   */
  viewShowing?: boolean;

  /**
   * Hide the current view. Called when the back button is clicked, if the view
   *  was showing.
   */
  hideView?: () => void;

  // -------------- Edit Form Support --------------
  /**
   * The title to use for the edit form (if applicable).
   */
  editTitle?: string;

  /**
   * The form for editing data. If omitted, the add functionality is disabled.
   */
  editForm?: ReactNode;

  /**
   * True, if the edit should show. False, otherwise. Should be controlled
   *  by the calling code.
   */
  editShowing?: boolean;

  /**
   * Hide the current edit. Called when the back button is clicked, if the edit
   *  was showing.
   */
  hideEdit?: () => void;
}

/**
 * State details for within the component
 */
interface StateDetails {
  formTitle: string;
  content: ReactNode;
}

/**
 * A component to control data. It contains a grid and an add form.
 */
const DataControl = (props: DataControlProps) => {
  /** The current mode the component is in, based on what's showing */
  const mode = useMemo<'grid' | 'view' | 'add' | 'edit'>(() => {
    if (props.addShowing == true) return 'add';
    if (props.editShowing == true) return 'edit';
    if (props.viewShowing == true) return 'view';
    return 'grid';
  }, [props.addShowing, props.editShowing, props.viewShowing]);

  /** True, if the grid is current hidden. False, otherwise. */
  const gridHidden = useMemo(() => mode != 'grid', [mode]);

  /** True, if the developer has provided an add form. False, otherwise */
  const hasAdd = useMemo(() => props.addForm != null, [props.addForm]);

  /** Action to take when the back button was clicked. */
  const backClick = useCallback(() => {
    // Determine hide action
    switch (mode) {
      case 'grid':
        throw Error('Back button should not show when mode is "grid"');
      case 'view':
        props.hideView!();
        break;
      case 'add':
        props.hideAdd!();
        break;
      case 'edit':
        props.hideEdit!();
        break;
    }

    // Determine additional actions
    if (mode == 'add' || mode == 'edit') {
      if (props.resetForm) props.resetForm();
    }
  }, [mode, props]);

  /** State details for the component based on the mode */
  const stateDetails = useMemo<StateDetails>(() => {
    switch (mode) {
      case 'grid':
        return {
          content: (
            <DataGrid
              columns={props.gridColumns}
              rows={props.rowData}
              hideFooterSelectedRowCount
              autoPageSize
              {...props.dataGridProps}
            />
          ),
          formTitle: props.pageTitle,
        };
      case 'view':
        return {
          content: props.viewContent!,
          formTitle: props.viewTitle!,
        };
      case 'add':
        return {
          content: props.addForm!,
          formTitle: props.addTitle!,
        };
      case 'edit':
        return {
          content: props.editForm!,
          formTitle: props.editTitle!,
        };
      default:
        throw Error(`Unrecognized mode: ${mode}`);
    }
  }, [
    mode,
    props.addForm,
    props.addTitle,
    props.dataGridProps,
    props.editForm,
    props.editTitle,
    props.gridColumns,
    props.pageTitle,
    props.rowData,
    props.viewContent,
    props.viewTitle,
  ]);

  return (
    <Box sx={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding={1.5}
      >
        <Box display="flex" alignItems="center">
          {gridHidden && (
            <Button
              color="secondary"
              variant="outlined"
              startIcon={<ArrowBackIosNewRoundedIcon />}
              onClick={backClick}
              sx={{ marginRight: '1em' }}
            >
              Back
            </Button>
          )}
          <Typography variant="h4">{stateDetails.formTitle}</Typography>
        </Box>
        {hasAdd && !gridHidden && !props.addShowing && (
          <Button
            color="primary"
            onClick={props.showAddForm!}
            variant="contained"
          >
            {props.addTitle}
          </Button>
        )}
      </Box>
      <Divider />
      <Box height="100%" position="relative" padding=".75em">
        {stateDetails.content}
      </Box>
    </Box>
  );
};

export default DataControl;
